// Finance Overview (2026-08-25 redesign).
//
// Redesigned onto the shared Components/Finance primitives so the
// whole Finance module reads from one visual system. All API calls,
// filters, permissions, and calculations are unchanged — the
// FinanceOverview endpoint + OutstandingReport endpoint drive the
// same numbers as before. Only the presentation was refactored.
//
// Structure:
//   FinancePageHeader   Location filter lives in the header actions.
//   5 × FinanceKpiCard  Bank / Cash / Revenue / Expenses / Net Profit.
//   FinanceSectionCard  Accounts (left)     · list rows with tone dot.
//   FinanceSectionCard  Outstanding (right) · big value + top rows.
//   Quick Actions strip Transactions / Expenses / Invoices / Reports /
//                       Record Payment (all real, permission-guarded
//                       destinations already registered in Routes).
import React, { useMemo } from "react";
import { Box, Typography, Grid, Chip, Skeleton, Button } from "@mui/material";
import { useGetFinanceOverviewQuery, useGetOutstandingReportQuery } from "../../Api/attoDeskApi";
import { useNavigate } from "react-router-dom";
import LocationFilter, { useLocationFilter, toLocationParam } from "./LocationFilter";
import dayjs from "dayjs";

import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentsIcon from "@mui/icons-material/Payments";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import SwapVertOutlinedIcon from "@mui/icons-material/SwapVertOutlined";
import PriceChangeOutlinedIcon from "@mui/icons-material/PriceChangeOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  FinancePageHeader, FinanceKpiCard, FinanceSectionCard, fin, toneStyles,
} from "../../Components/Finance";

const fmtMoney = (n: number) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const FinanceDashboard = () => {
  const navigate = useNavigate();

  const { locationId } = useLocationFilter();
  const locParams = toLocationParam(locationId);
  const { data: overviewData, isLoading } = useGetFinanceOverviewQuery(locParams);
  const { data: outstandingData } = useGetOutstandingReportQuery(locParams);

  const ov = useMemo(() => (overviewData?.data as any) || {}, [overviewData]);
  const outstanding = useMemo(() => (outstandingData?.data as any) || {}, [outstandingData]);

  const accounts: any[] = ov.accounts || [];
  const bankBalance = accounts.filter((a: any) => a.type === "bank").reduce((s: number, a: any) => s + Number(a.current_balance || 0), 0);
  const cashBalance = accounts.filter((a: any) => a.type === "cash").reduce((s: number, a: any) => s + Number(a.current_balance || 0), 0);
  const monthlyIncome = Number(ov.monthlyIncome || 0);
  const monthlyExpenses = Number(ov.monthlyExpenses || 0);
  const profit = monthlyIncome - monthlyExpenses;
  const outstandingTotal = Number(outstanding.totalOutstanding || 0);
  const outstandingCount = Number(outstanding.invoiceCount || 0);
  const outstandingOverdue = Number(outstanding.overdueAmount || 0);

  if (isLoading) return (
    <Box sx={{ bgcolor: fin.color.pageBg, minHeight: "100%", mx: -3, mt: -3, px: 3, pt: 3, pb: 3 }}>
      <Skeleton variant="rectangular" height={96} sx={{ borderRadius: `${fin.radius.lg}px`, mb: 2.5 }} />
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Grid item lg={2.4} md={4} sm={6} xs={12} key={i}>
            <Skeleton variant="rectangular" height={110} sx={{ borderRadius: `${fin.radius.md}px` }} />
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={2}>
        <Grid item md={7} xs={12}><Skeleton variant="rectangular" height={280} sx={{ borderRadius: `${fin.radius.md}px` }} /></Grid>
        <Grid item md={5} xs={12}><Skeleton variant="rectangular" height={280} sx={{ borderRadius: `${fin.radius.md}px` }} /></Grid>
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: fin.color.pageBg, minHeight: "100%", mx: -3, mt: -3, px: 3, pt: 3, pb: 3 }}>
      <FinancePageHeader
        title="Financial Overview"
        description="Monitor balances, revenue, expenses, invoices, and account activity."
        icon={<AccountBalanceIcon />}
        actions={<LocationFilter />}
        meta={
          <Chip
            size="small"
            icon={<CalendarTodayIcon sx={{ fontSize: "14px !important", color: `${fin.color.blue} !important` }} />}
            label={dayjs().format("MMMM YYYY")}
            sx={{ height: 24, fontSize: 12, fontWeight: 800, bgcolor: fin.color.card, color: fin.color.navy, border: `1px solid ${fin.color.border}` }}
          />
        }
      />

      {/* KPI Row */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item lg={2.4} md={4} sm={6} xs={12}>
          <FinanceKpiCard label="Bank Balance" value={fmtMoney(bankBalance)}
            icon={<AccountBalanceIcon />} tone="steel" hint={accounts.filter((a: any) => a.type === "bank").length + " account(s)"} />
        </Grid>
        <Grid item lg={2.4} md={4} sm={6} xs={12}>
          <FinanceKpiCard label="Cash Balance" value={fmtMoney(cashBalance)}
            icon={<AccountBalanceWalletIcon />} tone="blue" hint={accounts.filter((a: any) => a.type === "cash").length + " account(s)"} />
        </Grid>
        <Grid item lg={2.4} md={4} sm={6} xs={12}>
          <FinanceKpiCard label="Revenue" value={fmtMoney(monthlyIncome)}
            icon={<TrendingUpIcon />} tone="success" hint="This month" />
        </Grid>
        <Grid item lg={2.4} md={4} sm={6} xs={12}>
          <FinanceKpiCard label="Expenses" value={fmtMoney(monthlyExpenses)}
            icon={<TrendingDownIcon />} tone="danger" hint="This month" />
        </Grid>
        <Grid item lg={2.4} md={4} sm={6} xs={12}>
          <FinanceKpiCard label="Net Profit" value={fmtMoney(profit)}
            icon={<ShowChartIcon />} tone={profit >= 0 ? "success" : "danger"} hint="This month" />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Accounts */}
        <Grid item lg={7} md={12} xs={12}>
          <FinanceSectionCard
            title="Accounts"
            subtitle={accounts.length === 0 ? "No accounts configured." : `${accounts.length} account${accounts.length === 1 ? "" : "s"} · Balances by ledger`}
            icon={<AccountBalanceIcon />}
            tone="steel"
          >
            {accounts.length === 0 ? (
              <Box sx={{ py: 3, textAlign: "center" }}>
                <Typography sx={{ fontSize: 13, color: fin.color.muted }}>No accounts configured</Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {accounts.map((acc: any) => {
                  const positive = Number(acc.current_balance || 0) >= 0;
                  const dotTone = acc.type === "bank" ? toneStyles("steel") : toneStyles("blue");
                  return (
                    <Box key={acc.id} sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      p: 1.5, borderRadius: `${fin.radius.sm}px`,
                      bgcolor: fin.color.card, border: `1px solid ${fin.color.border}`,
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                        <Box sx={{
                          width: 34, height: 34, borderRadius: `${fin.radius.sm}px`,
                          bgcolor: dotTone.bg, color: dotTone.fg,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}>
                          {acc.type === "bank" ? <AccountBalanceIcon sx={{ fontSize: 18 }} /> : <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: fin.color.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {acc.name}
                          </Typography>
                          <Chip label={String(acc.type || "").toUpperCase()} size="small"
                            sx={{ height: 18, fontSize: 9.5, fontWeight: 800, letterSpacing: "0.06em", bgcolor: dotTone.bg, color: dotTone.fg, border: `1px solid ${fin.color.border}`, mt: 0.35 }} />
                        </Box>
                      </Box>
                      <Typography sx={{
                        fontSize: 15, fontWeight: 800,
                        color: positive ? fin.color.successText : fin.color.dangerText,
                        fontVariantNumeric: "tabular-nums",
                      }}>
                        {fmtMoney(Number(acc.current_balance || 0))}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </FinanceSectionCard>
        </Grid>

        {/* Outstanding Invoices */}
        <Grid item lg={5} md={12} xs={12}>
          <FinanceSectionCard
            title="Outstanding Invoices"
            subtitle={outstandingCount > 0 ? `${outstandingCount} invoice${outstandingCount === 1 ? "" : "s"} outstanding` : "All invoices settled"}
            icon={<WarningAmberIcon />}
            tone={outstandingTotal > 0 ? "warning" : "success"}
            action={
              <Button size="small"
                endIcon={<ArrowForwardIcon sx={{ fontSize: "14px !important" }} />}
                onClick={() => navigate("/invoice/list")}
                sx={{ textTransform: "none", fontWeight: 700, fontSize: 12, color: fin.color.blue }}>
                View All
              </Button>
            }
          >
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: outstandingOverdue > 0 ? 1 : 0 }}>
              <Typography sx={{
                fontSize: 30, fontWeight: 800, lineHeight: 1, letterSpacing: "-0.02em",
                color: outstandingTotal > 0 ? fin.color.warningText : fin.color.successText,
                fontVariantNumeric: "tabular-nums",
              }}>
                {fmtMoney(outstandingTotal)}
              </Typography>
              {outstandingOverdue > 0 && (
                <Chip
                  label={`${fmtMoney(outstandingOverdue)} overdue`}
                  size="small"
                  sx={{ height: 22, fontSize: 11, fontWeight: 800, bgcolor: fin.color.dangerBg, color: fin.color.dangerText, border: `1px solid ${fin.color.border}` }}
                />
              )}
            </Box>

            {outstanding.byStudent?.length > 0 ? (
              <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column" }}>
                {(outstanding.byStudent as any[]).slice(0, 5).map((s: any, i: number, arr: any[]) => (
                  <Box key={s.studentId} sx={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    py: 1, borderBottom: i < arr.length - 1 ? `1px solid ${fin.color.borderSoft}` : "none",
                  }}>
                    <Typography sx={{ fontSize: 12.5, color: fin.color.navy, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: 1 }}>
                      {s.studentName}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: fin.color.warningText, fontVariantNumeric: "tabular-nums" }}>
                      {fmtMoney(Number(s.totalOutstanding))}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : outstandingCount === 0 ? (
              <Typography sx={{ mt: 1.5, fontSize: 12.5, color: fin.color.muted }}>
                Nothing outstanding for the selected location.
              </Typography>
            ) : null}
          </FinanceSectionCard>
        </Grid>

        {/* Quick Actions strip */}
        <Grid item xs={12}>
          <FinanceSectionCard title="Quick Finance Actions" icon={<SwapVertOutlinedIcon />} tone="indigo" padding={2}>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" },
              gap: 1,
            }}>
              {[
                { label: "Transactions",   desc: "Ledger + filters",      icon: <SwapVertOutlinedIcon />,    path: "/finance/transactions",  tone: "steel" as const },
                { label: "Record Payment", desc: "New payment entry",     icon: <PaymentsIcon />,            path: "/payment/create",        tone: "success" as const },
                { label: "Invoices",       desc: "Billing & AR",          icon: <ReceiptLongIcon />,         path: "/invoice/list",           tone: "warning" as const },
                { label: "Expenses",       desc: "Track spending",        icon: <PriceChangeOutlinedIcon />, path: "/finance/expenses",       tone: "danger"  as const },
                { label: "Reports",        desc: "Analyse cash flow",     icon: <AssessmentOutlinedIcon />,  path: "/finance/reports",        tone: "indigo"  as const },
              ].map((a) => {
                const t = toneStyles(a.tone);
                return (
                  <Box
                    key={a.path}
                    role="button" tabIndex={0}
                    onClick={() => navigate(a.path)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(a.path); } }}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.25,
                      p: 1.25, borderRadius: `${fin.radius.sm}px`,
                      cursor: "pointer",
                      bgcolor: fin.color.card, border: `1px solid ${fin.color.border}`,
                      transition: "border-color 0.15s, box-shadow 0.15s, transform 0.15s",
                      outline: "none",
                      "&:hover": { borderColor: t.fg, boxShadow: fin.shadow.card, transform: "translateY(-1px)" },
                      "&:focus-visible": { outline: `2px solid ${fin.color.blue}`, outlineOffset: 2 },
                    }}
                  >
                    <Box sx={{
                      width: 34, height: 34, borderRadius: `${fin.radius.sm}px`,
                      bgcolor: t.bg, color: t.fg,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                      "& svg": { fontSize: 18 },
                    }}>{a.icon}</Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: fin.color.navy, whiteSpace: "nowrap" }}>
                        {a.label}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: fin.color.muted, whiteSpace: "nowrap" }}>
                        {a.desc}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </FinanceSectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FinanceDashboard;
